import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { NumericFormat, NumberFormatValues } from "react-number-format"
import {
  isNoOpAfterNormalization,
  normalizeNumericInputValue,
} from "utils/decimalInput"
import {
  DetailsTrailing,
  StyledInput,
  StyledLabel,
  StyledTextFieldFormItem,
} from "./styled"

interface IProps {
  label: string
  value: null | number
  onChange?: (value: null | number) => void
  placeholder?: string
  detailTrailing?: string
  isError?: boolean
  disabled?: boolean
  readOnly?: boolean
  onFocus?: () => void
  onEnter?: () => void
  min?: number
  max?: number
  decimals?: number
}

const NumericInput = (props: IProps) => {
  const {
    detailTrailing,
    isError = false,
    disabled = false,
    readOnly = false,
    onFocus,
    onChange,
    placeholder,
    value,
    label,
    onEnter,
    min,
    max,
    decimals = 0,
  } = props

  const [internalValue, setInternalValue] = useState(value?.toString() ?? "")

  useEffect(() => {
    const hasNumberValue = value !== null && !isNaN(value)
    const isValueChanged = value !== Number(internalValue)

    if (hasNumberValue && isValueChanged) {
      setInternalValue(value.toString())
    }
  }, [value])

  const inputRef = useRef<HTMLInputElement>(null)

  const onValueChange = (values: NumberFormatValues) => {
    if (disabled) return

    const normalized = normalizeNumericInputValue(values.value)

    if (normalized === "") {
      setInternalValue("")
      onChange?.(null)
      return
    }

    setInternalValue(normalized)

    if (values.floatValue !== undefined && values.floatValue !== value) {
      onChange?.(values.floatValue)
    }
  }

  const isAllowed = (values: NumberFormatValues) => {
    const normalized = normalizeNumericInputValue(values.value)
    if (isNoOpAfterNormalization(values.value, normalized, internalValue))
      return false

    const floatValue = values.floatValue
    const isMinValid =
      min === undefined || floatValue === undefined || floatValue >= min
    const isMaxValid =
      max === undefined || floatValue === undefined || floatValue <= max

    return isMinValid && isMaxValid
  }

  const trailingRef = useRef<HTMLDivElement>(null)
  const [trailingWidth, setTrailWidth] = useState(0)

  useLayoutEffect(() => {
    setTrailWidth(trailingRef.current?.getBoundingClientRect().width ?? 0)
  }, [trailingRef, detailTrailing])

  return (
    <StyledTextFieldFormItem
      style={
        {
          "--input-padding-right": "var(--input-padding)",
          "--input-trailing-padding-left": detailTrailing ? "4px" : "0px",
          "--detail-trailing-width": `${trailingWidth}px`,
        } as React.CSSProperties
      }
    >
      <StyledLabel>{label}</StyledLabel>

      <NumericFormat
        decimalScale={decimals}
        isAllowed={isAllowed}
        customInput={StyledInput}
        disabled={disabled}
        getInputRef={inputRef}
        inputMode="numeric"
        allowNegative={min === undefined || min < 0}
        thousandSeparator=","
        $disabled={disabled}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            setTimeout(() => {
              onEnter?.()
            }, 0)
          }
        }}
        onFocus={onFocus}
        readOnly={readOnly}
        $isError={isError}
        placeholder={placeholder}
        value={internalValue ?? ""}
        valueIsNumericString
        onValueChange={onValueChange}
        onBlur={() => {
          if (internalValue === "") {
            onChange?.(0)
            setInternalValue("0")
          }
        }}
        autoComplete="off"
        style={{
          paddingRight:
            "calc(var(--input-padding-right) + var(--input-trailing-padding-left) + var(--detail-trailing-width))",
        }}
      />
      {detailTrailing && <DetailsTrailing>{detailTrailing}</DetailsTrailing>}

      {detailTrailing && (
        <DetailsTrailing style={{ visibility: "hidden" }} ref={trailingRef}>
          {detailTrailing}
        </DetailsTrailing>
      )}
    </StyledTextFieldFormItem>
  )
}

export default NumericInput
